import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  TextField,
  Button
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  GitHub as GitHubIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

const Footer = () => {
  const { t } = useTranslation('nav');
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: t('footer.linkFeatures'), href: '/features' },
      { label: t('footer.linkPricing'), href: '/pricing' },
      { label: t('footer.linkForUniversities'), href: '/for-universities' },
      { label: t('footer.linkForTeachers'), href: '/for-teachers' },
      { label: t('footer.linkForStudents'), href: '/for-students' }
    ],
    resources: [
      { label: t('footer.linkDocumentation'), href: '/docs' },
      { label: t('footer.linkTutorials'), href: '/tutorials' },
      { label: t('footer.linkBlog'), href: '/blog' },
      { label: t('footer.linkSupport'), href: '/support' },
      { label: t('footer.linkApi'), href: '/api' }
    ],
    company: [
      { label: t('footer.linkAboutUs'), href: '/about' },
      { label: t('footer.linkCareers'), href: 'https://www.jobintr.com', external: true },
      { label: t('footer.linkPress'), href: '/press' },
      { label: t('footer.linkContact'), href: '/contact' },
      { label: t('footer.linkPrivacyPolicy'), href: '/privacy' },
      { label: t('footer.linkTermsOfService'), href: '/terms' }
    ]
  };

  const socialLinks = [
    { icon: <FacebookIcon />, href: 'https://facebook.com', label: 'Facebook' },
    { icon: <TwitterIcon />, href: 'https://twitter.com', label: 'Twitter' },
    { icon: <LinkedInIcon />, href: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: <InstagramIcon />, href: 'https://instagram.com', label: 'Instagram' },
    { icon: <YouTubeIcon />, href: 'https://youtube.com', label: 'YouTube' },
    { icon: <GitHubIcon />, href: 'https://github.com', label: 'GitHub' }
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 'auto'
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer */}
        <Grid container spacing={4} sx={{ py: 6 }}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Vitrine LMS
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('footer.tagline')}
            </Typography>
            
            {/* Contact Info */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                <Link href="mailto:contact@vitrinedev.site" variant="body2" color="text.secondary"
                  sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
                  contact@vitrinedev.site
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                <Link href="mailto:support@vitrinedev.site" variant="body2" color="text.secondary"
                  sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
                  support@vitrinedev.site
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('footer.location')}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Product Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('footer.productHeading')}
            </Typography>
            {footerLinks.product.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 1,
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                {link.label}
              </Link>
            ))}
          </Grid>

          {/* Resources Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('footer.resourcesHeading')}
            </Typography>
            {footerLinks.resources.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 1,
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                {link.label}
              </Link>
            ))}
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('footer.companyHeading')}
            </Typography>
            {footerLinks.company.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 1,
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                {link.label}
              </Link>
            ))}
          </Grid>

          {/* Newsletter */}
          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t('footer.stayUpdated')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('footer.newsletterDesc')}
            </Typography>
            <TextField
              size="small"
              placeholder={t('footer.emailPlaceholder')}
              variant="outlined"
              sx={{ mb: 1 }}
              fullWidth
            />
            <Button variant="contained" fullWidth size="small">
              {t('footer.subscribe')}
            </Button>
          </Grid>
        </Grid>

        <Divider />

        {/* Bottom Bar */}
        <Box
          sx={{
            py: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 0 } }}>
            {t('footer.rightsReserved', { year: currentYear })}
          </Typography>

          {/* Social Links */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {socialLinks.map((social) => (
              <IconButton
                key={social.label}
                component="a"
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
                aria-label={social.label}
              >
                {social.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
